import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type Project from 'src/entities/project';
import useUpdateProjectPermissionsMutation from '@projects/queries/useUpdateProjectPermissionsMutation';
import useSnackbarSystem from '@ui/systems/useSnackbarSystem';
import { trackGuestLinkSettingsChanged } from 'src/services/tracker/trackers/nac';
import { QueryKeys } from '@projects/queries/queryKeys';

type UseViewOnlyLinkProps = {
	project: Project;
	withDownloads: boolean;
};

const useViewOnlyLink = ( { project, withDownloads }: UseViewOnlyLinkProps ) => {
	const [ showSuccessMessage, setShowSuccessMessage ] = useState( false );
	const snackbarSystem = useSnackbarSystem();
	const queryClient = useQueryClient();
	const queryKey = QueryKeys.forViewOnlyLinkEnabled( project.id );

	const { data: enabled = project.viewOnlyLinkEnabled } = useQuery(
		queryKey,
		() => Promise.resolve( project.viewOnlyLinkEnabled ),
		{ initialData: project.viewOnlyLinkEnabled, staleTime: Infinity }
	);

	const { mutate: updateProjectPermissions } = useUpdateProjectPermissionsMutation( {
		projectID: project.id
	} );

	const viewOnlyLink = `${document.location.origin}/${project.shareLink( { withDownloads } )}`;

	const copyShareLink = useCallback( () => {
		navigator.clipboard.writeText( viewOnlyLink );
		setShowSuccessMessage( true );
		setTimeout( () => setShowSuccessMessage( false ), 2000 );
	}, [ viewOnlyLink ] );

	const boxStyles = {
		borderRadius: 4,
		backgroundColor: enabled
			? 'var(--color-bg-surface-container-blue)'
			: 'var(--color-bg-container)',
		border: enabled
			? '1px solid var(--color-outline-light-blue)'
			: '1px solid var(--color-outline-light)'
	};

	const onLinkEnabledToggled = useCallback( () => {
		const newEnabledState = !enabled;
		queryClient.setQueryData( queryKey, newEnabledState );

		updateProjectPermissions( { view_only_link_enabled: newEnabledState }, {
			onSuccess: () => {
				trackGuestLinkSettingsChanged( { projectID: project.id, enabled: newEnabledState, role: 'ViewOnly' } );

				if ( !newEnabledState ) {
					snackbarSystem.showInfoMessage( {
						title: 'Link disabled',
						description: 'No one will be able to access the project with this link.'
					} );
				}
			},
			onError: () => queryClient.setQueryData( queryKey, !newEnabledState )
		} );
	}, [ enabled, queryKey, queryClient, project.id, updateProjectPermissions, snackbarSystem ] );

	return {
		enabled,
		showSuccessMessage,
		copyShareLink,
		boxStyles,
		onLinkEnabledToggled
	};
};

export default useViewOnlyLink;
